import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fmtCurrency, fmtDate, fmtTime } from '../lib/utils';
import { Card, Stat, SH, Avatar, Badge, Btn, Modal, Field, Input, Select, Textarea, Grid4, PageHeader, Empty, Spinner, Toast, ProgressBar, B } from '../components/UI';
import { useAuth } from '../context/AuthContext';

// ════════════════════════════════════════════════════════
//  CUSTOMERS PAGE
// ════════════════════════════════════════════════════════
export function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [{ data: custs }, { data: j }] = await Promise.all([
      supabase.from('profiles').select('*').eq('role', 'customer').order('full_name'),
      supabase.from('jobs').select('id,customer_id,customer_name,type,status,contract_total'),
    ]);
    setCustomers(custs || []);
    setJobs(j || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Customers" action={<Btn variant="primary" onClick={() => setModal('new')}>+ Add Customer</Btn>} />
      {customers.length === 0 && <Empty icon="👤" title="No customers yet" sub="Add your first customer to get started." action={<Btn variant="primary" onClick={() => setModal('new')}>+ Add Customer</Btn>} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '1rem' }}>
        {customers.map(c => {
          const cJobs = jobs.filter(j => j.customer_id === c.id);
          const total = cJobs.reduce((s, j) => s + (j.contract_total || 0), 0);
          return (
            <Card key={c.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
                <Avatar name={c.full_name} size={44} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.full_name}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>📧 {c.email}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>📞 {c.phone || '—'}</div>
                </div>
                <Btn size="sm" variant="outline" onClick={() => setModal(c)}>✏ Edit</Btn>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                <Stat label="Jobs" value={cJobs.length} />
                <Stat label="Value" value={fmtCurrency(total)} accent="#16A34A" />
              </div>
              {cJobs.map(j => (
                <div key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: 6, border: '1px solid #F1F5F9', cursor: 'pointer', marginBottom: 4, fontSize: 12 }} onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontWeight: 700, color: B.info }}>{j.id?.slice(0, 8)}</span>
                  <span style={{ color: '#64748B' }}>{j.type}</span>
                  <Badge status={j.status} />
                </div>
              ))}
              {cJobs.length === 0 && <div style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', padding: 8 }}>No jobs yet</div>}
            </Card>
          );
        })}
      </div>
      {modal && (
        <UserModal
          user={modal === 'new' ? null : modal}
          role="customer"
          onClose={() => setModal(null)}
          onSave={() => { load(); setModal(null); setToast({ msg: modal === 'new' ? 'Customer added ✓' : 'Customer updated ✓', type: 'success' }); }}
          onToast={t => setToast(t)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════
//  SHARED USER MODAL (admin + customer)
// ════════════════════════════════════════════════════════
function UserModal({ user, role, onClose, onSave, onToast }) {
  const isAdmin = role === 'admin';
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    title: user?.title || '',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!form.full_name || !form.email) { onToast({ msg: 'Name and email required.', type: 'error' }); return; }
    setSaving(true);
    try {
      if (user) {
        // Update existing profile
        const { error } = await supabase.from('profiles').update({
          full_name: form.full_name,
          phone: form.phone,
          title: form.title,
        }).eq('id', user.id);
        if (error) throw error;
      } else {
        // Create new user — insert into profiles with a placeholder id
        // Since we can't call admin API from the client, we create via Supabase Auth signUp
        // then update the profile
        const { data, error } = await supabase.auth.signUp({
          email: form.email,
          password: form.password || 'TempPass123!',
          options: {
            data: { full_name: form.full_name, role },
          },
        });
        if (error) throw error;
        if (data?.user) {
          await supabase.from('profiles').upsert({
            id: data.user.id,
            email: form.email,
            full_name: form.full_name,
            phone: form.phone,
            title: form.title,
            role,
          });
        }
      }
      onSave();
    } catch (err) {
      onToast({ msg: 'Error: ' + err.message, type: 'error' });
    }
    setSaving(false);
  }

  return (
    <Modal
      title={user ? `Edit ${isAdmin ? 'Admin' : 'Customer'}` : `Add ${isAdmin ? 'Admin User' : 'Customer'}`}
      onClose={onClose}
      footer={
        <>
          {user && <Btn variant="danger" onClick={async () => {
            if (!window.confirm('Delete this user?')) return;
            await supabase.from('profiles').delete().eq('id', user.id);
            onSave();
          }}>🗑 Delete</Btn>}
          <div style={{ flex: 1 }} />
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : user ? 'Save Changes' : `Add ${isAdmin ? 'Admin' : 'Customer'}`}</Btn>
        </>
      }
    >
      <Field label="Full Name" required><Input value={form.full_name} onChange={f('full_name')} placeholder="Jane Smith" /></Field>
      <Field label="Email" required><Input type="email" value={form.email} onChange={f('email')} placeholder="jane@email.com" disabled={!!user} /></Field>
      <Field label="Phone"><Input value={form.phone} onChange={f('phone')} placeholder="317-555-0000" /></Field>
      {isAdmin && <Field label="Title"><Input value={form.title} onChange={f('title')} placeholder="e.g. Project Manager" /></Field>}
      {!user && <Field label="Password"><Input type="password" value={form.password} onChange={f('password')} placeholder="Minimum 6 characters" /></Field>}
      {!user && (
        <div style={{ padding: '10px 12px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A', fontSize: 12, color: '#78350F', marginTop: 8 }}>
          📧 After adding, give the user the website URL and their email + password to log in.
          {!form.password && ' Default password: TempPass123! (change after first login)'}
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════
//  CREW PAGE
// ════════════════════════════════════════════════════════
export function CrewPage() {
  const [crew, setCrew] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [{ data: c }, { data: j }] = await Promise.all([
      supabase.from('crew').select('*').order('name'),
      supabase.from('jobs').select('id,hours,status'),
    ]);
    setCrew(c || []);
    setJobs(j || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Crew" action={<Btn variant="primary" onClick={() => setModal('new')}>+ Add Member</Btn>} />
      {crew.length === 0 && <Empty icon="👷" title="No crew members yet" action={<Btn variant="primary" onClick={() => setModal('new')}>+ Add Member</Btn>} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '1rem' }}>
        {crew.map(c => {
          const totH = jobs.flatMap(j => (j.hours || []).filter(h => h.name === c.name)).reduce((s, h) => s + (h.hours || 0), 0);
          const activeJobs = jobs.filter(j => ['in-progress','scheduled','final-inspection'].includes(j.status) && (j.hours || []).some(h => h.name === c.name)).length;
          return (
            <Card key={c.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
                <Avatar name={c.name} size={44} color={c.team === 'Alpha' ? B.amber : B.info} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>{c.role} · Team {c.team}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal(c)}>✏</Btn>
                  <Btn size="sm" variant="danger" onClick={async () => { if (window.confirm('Remove crew member?')) { await supabase.from('crew').delete().eq('id', c.id); load(); } }}>✕</Btn>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Stat label="Hrs" value={totH + ''} />
                <Stat label="Jobs" value={activeJobs + ''} accent={B.info} />
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8' }}>📞 {c.phone || '—'}</div>
            </Card>
          );
        })}
      </div>
      {modal && (
        <CrewModal
          member={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { load(); setModal(null); setToast({ msg: 'Saved ✓', type: 'success' }); }}
          onToast={t => setToast(t)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

function CrewModal({ member, onClose, onSave, onToast }) {
  const [form, setForm] = useState({ name: member?.name || '', role: member?.role || 'Installer', team: member?.team || 'Alpha', phone: member?.phone || '' });
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  async function save() {
    if (!form.name) { onToast({ msg: 'Name required.', type: 'error' }); return; }
    if (member) { await supabase.from('crew').update(form).eq('id', member.id); }
    else { await supabase.from('crew').insert(form); }
    onSave();
  }
  return (
    <Modal title={member ? 'Edit Crew Member' : 'Add Crew Member'} onClose={onClose}
      footer={<><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={save}>{member ? 'Save' : 'Add Member'}</Btn></>}
    >
      <Field label="Full Name" required><Input value={form.name} onChange={f('name')} /></Field>
      <Field label="Role"><Select value={form.role} onChange={f('role')}>{['Field Lead','Installer','Apprentice','Subcontractor'].map(r => <option key={r}>{r}</option>)}</Select></Field>
      <Field label="Team"><Select value={form.team} onChange={f('team')}>{['Alpha','Beta','Gamma','Solo'].map(t => <option key={t}>{t}</option>)}</Select></Field>
      <Field label="Phone"><Input value={form.phone} onChange={f('phone')} placeholder="317-555-0000" /></Field>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════
//  INVOICES PAGE — with standalone invoice creator
// ════════════════════════════════════════════════════════
export function InvoicesPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [{ data: j }, { data: c }] = await Promise.all([
      supabase.from('jobs').select('id,customer_id,customer_name,invoices,contract_total,amount_paid'),
      supabase.from('profiles').select('id,full_name,email,phone').eq('role', 'customer').order('full_name'),
    ]);
    setJobs(j || []);
    setCustomers(c || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function markPaid(jobId, invId) {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    const inv = (job.invoices || []).find(i => i.id === invId);
    const updated = (job.invoices || []).map(i => i.id === invId ? { ...i, status: 'paid', paid_date: new Date().toISOString().slice(0, 10) } : i);
    await supabase.from('jobs').update({ invoices: updated, amount_paid: (job.amount_paid || 0) + (inv?.amount || 0) }).eq('id', jobId);
    setToast({ msg: 'Marked paid! ✓', type: 'success' });
    load();
  }

  if (loading) return <Spinner />;

  const all = jobs.flatMap(j => (j.invoices || []).map(i => ({ ...i, job_id: j.id, customer: j.customer_name })));
  const pending = all.filter(i => i.status === 'pending');
  const paid = all.filter(i => i.status === 'paid');
  const totalPending = pending.reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = paid.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Invoices" action={<Btn variant="primary" onClick={() => setShowCreate(true)}>+ New Invoice</Btn>} />
      <Grid4 style={{ marginBottom: '1.5rem' }}>
        <Stat label="Total" value={all.length} />
        <Stat label="Pending" value={pending.length} accent="#D97706" />
        <Stat label="Paid" value={paid.length} accent="#16A34A" />
        <Stat label="Outstanding" value={fmtCurrency(totalPending)} accent="#DC2626" />
      </Grid4>
      <Card style={{ marginBottom: '1rem' }}>
        <SH title="Pending Invoices" />
        {pending.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8' }}>All clear — no pending invoices!</p>}
        {pending.map(inv => (
          <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #FDE68A', borderRadius: 10, background: '#FFFBEB', marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{inv.description || inv.id} <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: 12 }}>· {inv.customer}</span></div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>Issued: {fmtDate(inv.date)}</div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", color: '#D97706' }}>{fmtCurrency(inv.amount)}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn size="sm" variant="success" onClick={() => markPaid(inv.job_id, inv.id)}>Mark Paid</Btn>
              <Btn size="sm" variant="outline" onClick={() => navigate(`/jobs/${inv.job_id}`)}>View Job</Btn>
            </div>
          </div>
        ))}
      </Card>
      <Card>
        <SH title={`Paid (${paid.length})`} action={<strong style={{ fontSize: 14, color: '#16A34A' }}>{fmtCurrency(totalPaid)}</strong>} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Description','Job','Customer','Amount','Paid Date'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>{h}</th>)}</tr></thead>
            <tbody>
              {paid.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>No paid invoices yet.</td></tr>}
              {paid.map(inv => (
                <tr key={inv.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '9px 10px', fontWeight: 600 }}>{inv.description || inv.id}</td>
                  <td style={{ padding: '9px 10px' }}><button onClick={() => navigate(`/jobs/${inv.job_id}`)} style={{ color: B.info, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', fontSize: 13, fontFamily: "'Barlow',sans-serif" }}>{inv.job_id?.slice(0, 8)}</button></td>
                  <td style={{ padding: '9px 10px' }}>{inv.customer}</td>
                  <td style={{ padding: '9px 10px', fontWeight: 700, color: '#16A34A' }}>{fmtCurrency(inv.amount)}</td>
                  <td style={{ padding: '9px 10px', color: '#94A3B8' }}>{fmtDate(inv.paid_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {showCreate && (
        <CreateInvoiceModal
          jobs={jobs}
          customers={customers}
          onClose={() => setShowCreate(false)}
          onSave={(jobId, inv) => {
            const job = jobs.find(j => j.id === jobId);
            if (!job) return;
            const updated = [...(job.invoices || []), inv];
            supabase.from('jobs').update({ invoices: updated }).eq('id', jobId).then(() => {
              load();
              setShowCreate(false);
              setToast({ msg: 'Invoice created ✓', type: 'success' });
            });
          }}
          onToast={t => setToast(t)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

// ── Standalone Invoice Creator ────────────────────────────────────────────────
function CreateInvoiceModal({ jobs, customers, onClose, onSave, onToast }) {
  const [custId, setCustId] = useState('');
  const [jobId, setJobId] = useState('');
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState([{ desc: '', qty: 1, price: '' }]);
  const [useLines, setUseLines] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const customerJobs = jobs.filter(j => j.customer_id === custId);
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.price) || 0), 0);
  const taxAmt = subtotal * (taxRate / 100);
  const lineTotal = subtotal + taxAmt;
  const finalAmount = useLines ? lineTotal : (parseFloat(amount) || 0);

  function addLine() { setLines(l => [...l, { desc: '', qty: 1, price: '' }]); }
  function removeLine(i) { setLines(l => l.filter((_, idx) => idx !== i)); }
  function updateLine(i, k, v) { setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln)); }

  async function save() {
    if (!custId) { onToast({ msg: 'Please select a customer.', type: 'error' }); return; }
    if (!jobId) { onToast({ msg: 'Please select a job to attach this invoice to.', type: 'error' }); return; }
    if (!finalAmount) { onToast({ msg: 'Amount is required.', type: 'error' }); return; }
    setSaving(true);
    const customer = customers.find(c => c.id === custId);
    const inv = {
      id: 'inv-' + Date.now(),
      date: new Date().toISOString().slice(0, 10),
      due_date: dueDate || null,
      amount: finalAmount,
      status: 'pending',
      description: desc || (useLines && lines[0]?.desc) || 'Invoice',
      notes,
      paid_date: '',
      lines: useLines ? lines : [],
      tax_rate: taxRate,
    };
    onSave(jobId, inv);
    setSaving(false);
  }

  function printPreview() {
    const customer = customers.find(c => c.id === custId);
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Invoice</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;padding:40px;color:#0F172A;}
    .header{display:flex;justify-content:space-between;padding-bottom:20px;border-bottom:3px solid #0F172A;margin-bottom:24px;}
    .co{font-size:24px;font-weight:800;}.inv-title{font-size:32px;font-weight:800;text-align:right;}
    .inv-num{color:#F59E0B;font-weight:700;text-align:right;}.bill{background:#F8FAFC;padding:16px;border-radius:8px;margin-bottom:24px;}
    table{width:100%;border-collapse:collapse;margin-bottom:20px;}th{background:#0F172A;color:#fff;padding:10px;text-align:left;font-size:12px;}
    td{padding:9px 10px;border-bottom:1px solid #F1F5F9;}tr:nth-child(even) td{background:#F8FAFC;}
    .totals{display:flex;justify-content:flex-end;}.totals-inner{min-width:220px;}
    .tr{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #F1F5F9;font-size:13px;}
    .grand{border-top:2px solid #0F172A;border-bottom:none;font-size:16px;font-weight:800;padding-top:10px;}
    .footer{border-top:1px solid #E2E8F0;margin-top:24px;padding-top:12px;font-size:11px;color:#94A3B8;text-align:center;}
    @media print{button{display:none;}}</style></head><body>
    <div class="header"><div><div class="co">🦅 NIGHTHAWK ROOFING</div><div style="font-size:12px;color:#64748B;margin-top:6px">nighthawkroofing.com</div></div>
    <div><div class="inv-title">INVOICE</div><div class="inv-num">INV-${Date.now().toString().slice(-6)}</div>
    <div style="font-size:12px;color:#64748B;text-align:right;margin-top:4px">Date: ${fmtDate(new Date().toISOString().slice(0,10))}</div>
    ${dueDate ? `<div style="font-size:12px;color:#64748B;text-align:right">Due: ${fmtDate(dueDate)}</div>` : ''}</div></div>
    <div class="bill"><div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#94A3B8;margin-bottom:4px">Bill To</div>
    <div style="font-size:15px;font-weight:700">${customer?.full_name || ''}</div>
    <div style="font-size:12px;color:#64748B">${customer?.email || ''} ${customer?.phone ? '| ' + customer.phone : ''}</div></div>
    ${useLines ? `<table><thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr></thead><tbody>
    ${lines.filter(l => l.desc || l.price).map(l => `<tr><td>${l.desc}</td><td>${l.qty}</td><td>$${parseFloat(l.price||0).toFixed(2)}</td><td style="font-weight:700">$${((parseFloat(l.qty)||0)*(parseFloat(l.price)||0)).toFixed(2)}</td></tr>`).join('')}
    </tbody></table>
    <div class="totals"><div class="totals-inner">
    <div class="tr"><span>Subtotal</span><span>$${subtotal.toFixed(2)}</span></div>
    <div class="tr"><span>Tax (${taxRate}%)</span><span>$${taxAmt.toFixed(2)}</span></div>
    <div class="tr grand"><span>Total Due</span><span>$${lineTotal.toFixed(2)}</span></div>
    </div></div>` : `<div style="padding:20px;background:#F8FAFC;border-radius:8px;margin-bottom:20px;display:flex;justify-content:space-between;font-size:16px;"><span>${desc || 'Invoice'}</span><span style="font-weight:800">$${parseFloat(amount||0).toFixed(2)}</span></div>`}
    ${notes ? `<div style="background:#FFFBEB;padding:12px;border-radius:8px;border:1px solid #FDE68A;font-size:12px;margin-bottom:16px"><strong>Notes:</strong> ${notes}</div>` : ''}
    <div class="footer">Thank you for choosing Nighthawk Roofing. Payment is due within 30 days.</div>
    <div style="text-align:center;margin-top:20px"><button onclick="window.print()" style="padding:10px 24px;background:#0F172A;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer">🖨 Print / Save as PDF</button></div>
    </body></html>`;
    const w = window.open('', '_blank', 'width=820,height=1060');
    if (w) { w.document.write(html); w.document.close(); }
    else onToast({ msg: 'Allow popups to preview the invoice.', type: 'error' });
  }

  return (
    <Modal title="Create New Invoice" onClose={onClose} wide
      footer={
        <>
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn variant="info" onClick={printPreview}>👁 Preview / Print</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Invoice'}</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
        <Field label="Customer" required span2>
          <Select value={custId} onChange={e => { setCustId(e.target.value); setJobId(''); }}>
            <option value="">— Select customer —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </Select>
        </Field>
        <Field label="Link to Job" required span2>
          <Select value={jobId} onChange={e => setJobId(e.target.value)}>
            <option value="">— Select job —</option>
            {customerJobs.map(j => <option key={j.id} value={j.id}>{j.id?.slice(0,8)} — {j.customer_name}</option>)}
          </Select>
          {custId && customerJobs.length === 0 && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>No jobs found for this customer.</div>}
        </Field>
        <Field label="Description"><Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. 50% deposit" /></Field>
        <Field label="Due Date"><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0 8px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={useLines} onChange={e => setUseLines(e.target.checked)} />
          Use line items (itemized invoice)
        </label>
      </div>

      {!useLines ? (
        <Field label="Total Amount ($)" required>
          <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
      ) : (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
          <SH title="Line Items" action={<Btn size="sm" variant="outline" onClick={addLine}>+ Add Line</Btn>} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }}>
            <thead><tr>{['Description','Qty','Unit Price','Total',''].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0' }}>{h}</th>)}</tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 4px' }}><Input value={l.desc} onChange={e => updateLine(i, 'desc', e.target.value)} placeholder="Description" /></td>
                  <td style={{ padding: '4px 4px', width: 70 }}><Input type="number" value={l.qty} onChange={e => updateLine(i, 'qty', e.target.value)} min={0} /></td>
                  <td style={{ padding: '4px 4px', width: 100 }}><Input type="number" value={l.price} onChange={e => updateLine(i, 'price', e.target.value)} placeholder="0.00" /></td>
                  <td style={{ padding: '4px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>${((parseFloat(l.qty)||0)*(parseFloat(l.price)||0)).toFixed(2)}</td>
                  <td style={{ padding: '4px 4px' }}><Btn size="sm" variant="danger" onClick={() => removeLine(i)}>✕</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
              Tax %: <input type="number" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value)||0)} step="0.1" style={{ width: 60, padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }} />
            </label>
            <div style={{ fontSize: 13, color: '#64748B' }}>Subtotal: <strong>{fmtCurrency(subtotal)}</strong></div>
            {taxRate > 0 && <div style={{ fontSize: 13, color: '#64748B' }}>Tax: <strong>{fmtCurrency(taxAmt)}</strong></div>}
            <div style={{ fontSize: 15, fontWeight: 800, color: B.navy }}>Total: {fmtCurrency(lineTotal)}</div>
          </div>
        </div>
      )}

      <Field label="Notes / Memo">
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment instructions, project details, etc." style={{ minHeight: 56 }} />
      </Field>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════
//  MATERIALS PAGE
// ════════════════════════════════════════════════════════
export function MaterialsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('jobs').select('id,customer_name,materials').then(({ data }) => {
      setJobs(data || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Spinner />;

  const all = jobs.flatMap(j => (j.materials || []).map(m => ({ ...m, job_id: j.id, customer: j.customer_name })));
  const total = all.reduce((s, m) => s + (m.qty || 0) * (m.unit_cost || 0), 0);
  const statuses = ['not-ordered', 'ordered', 'delivered'];
  const statusConfig = { 'not-ordered': { label: 'Not Ordered', color: '#94A3B8' }, ordered: { label: 'Ordered', color: B.info }, delivered: { label: 'Delivered', color: '#16A34A' } };

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Materials Overview" />
      <Grid4 style={{ marginBottom: '1.5rem' }}>
        <Stat label="Total Items" value={all.length} />
        <Stat label="Delivered" value={all.filter(m => m.status === 'delivered').length} accent="#16A34A" />
        <Stat label="On Order" value={all.filter(m => m.status === 'ordered').length} accent={B.info} />
        <Stat label="Total Value" value={fmtCurrency(total)} accent={B.navy} />
      </Grid4>
      {statuses.map(st => {
        const items = all.filter(m => m.status === st);
        if (!items.length) return null;
        return (
          <Card key={st} style={{ marginBottom: '1rem' }}>
            <SH title={`${statusConfig[st].label} (${items.length})`} />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Job','Customer','Material','Qty','Unit','Cost'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {items.map((m, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '9px 10px' }}><button onClick={() => navigate(`/jobs/${m.job_id}`)} style={{ color: B.info, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', fontSize: 13, fontFamily: "'Barlow',sans-serif" }}>{m.job_id?.slice(0, 8)}</button></td>
                      <td style={{ padding: '9px 10px' }}>{m.customer}</td>
                      <td style={{ padding: '9px 10px', fontWeight: 500 }}>{m.name}</td>
                      <td style={{ padding: '9px 10px', fontWeight: 700 }}>{m.qty}</td>
                      <td style={{ padding: '9px 10px', color: '#94A3B8' }}>{m.unit}</td>
                      <td style={{ padding: '9px 10px', fontWeight: 700 }}>{fmtCurrency((m.qty || 0) * (m.unit_cost || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════
//  SETTINGS PAGE — with full admin user management
// ════════════════════════════════════════════════════════
export function SettingsPage() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: a }, { data: c }, { data: s }] = await Promise.all([
      supabase.from('profiles').select('*').eq('role', 'admin').order('full_name'),
      supabase.from('profiles').select('*').eq('role', 'customer').order('full_name'),
      supabase.from('company_settings').select('*').eq('id', 1).single(),
    ]);
    setAdmins(a || []);
    setCustomers(c || []);
    setSettings(s || { id: 1, name: 'Nighthawk Roofing', phone: '317-555-0100', email: 'info@nighthawkroofing.com', address: '', city: 'Indianapolis', state: 'IN', zip: '', website: '', license: '', insurance: '', labor_rate: 65, tax_rate: 0, deposit_pct: 50, invoice_footer: 'Thank you for choosing Nighthawk Roofing. Payment is due within 30 days.' });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveSettings() {
    setSaving(true);
    await supabase.from('company_settings').upsert(settings);
    setSaving(false);
    setToast({ msg: 'Settings saved ✓', type: 'success' });
  }

  function sf(k) { return e => setSettings(p => ({ ...p, [k]: e.target.value })); }

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Settings" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
        {/* Admin Users */}
        <Card>
          <SH title={`Admin Users (${admins.length})`} action={<Btn size="sm" variant="outline" onClick={() => setModal({ type: 'admin', user: null })}>+ Add Admin</Btn>} />
          {admins.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8' }}>No admin users yet.</p>}
          {admins.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
              <Avatar name={u.full_name} size={36} color="#6D28D9" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {u.full_name}
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#EDE9FE', color: '#6D28D9', marginLeft: 6 }}>Admin</span>
                  {u.id === currentUser?.id && <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 4 }}>(you)</span>}
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>{u.email}{u.title ? ` · ${u.title}` : ''}</div>
              </div>
              <Btn size="sm" variant="outline" onClick={() => setModal({ type: 'admin', user: u })}>✏</Btn>
            </div>
          ))}
        </Card>

        {/* Customers */}
        <Card>
          <SH title={`Customers (${customers.length})`} action={<Btn size="sm" variant="primary" onClick={() => setModal({ type: 'customer', user: null })}>+ Add Customer</Btn>} />
          {customers.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8' }}>No customers yet.</p>}
          {customers.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
              <Avatar name={u.full_name} size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{u.full_name}</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>{u.email} · {u.phone || '—'}</div>
              </div>
              <Btn size="sm" variant="outline" onClick={() => setModal({ type: 'customer', user: u })}>✏</Btn>
            </div>
          ))}
        </Card>
      </div>

      {/* Company Settings */}
      {settings && (
        <Card>
          <SH title="Company Settings" action={<Btn variant="primary" size="sm" onClick={saveSettings} disabled={saving}>💾 {saving ? 'Saving…' : 'Save Settings'}</Btn>} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <Field label="Company Name" required><Input value={settings.name || ''} onChange={sf('name')} /></Field>
            <Field label="Phone"><Input value={settings.phone || ''} onChange={sf('phone')} /></Field>
            <Field label="Email"><Input type="email" value={settings.email || ''} onChange={sf('email')} /></Field>
            <Field label="Website"><Input value={settings.website || ''} onChange={sf('website')} /></Field>
            <Field label="Street Address" span2><Input value={settings.address || ''} onChange={sf('address')} /></Field>
            <Field label="City"><Input value={settings.city || ''} onChange={sf('city')} /></Field>
            <Field label="State"><Input value={settings.state || ''} onChange={sf('state')} /></Field>
            <Field label="Zip"><Input value={settings.zip || ''} onChange={sf('zip')} /></Field>
            <Field label="License #"><Input value={settings.license || ''} onChange={sf('license')} /></Field>
            <Field label="Insurance / Bond #"><Input value={settings.insurance || ''} onChange={sf('insurance')} /></Field>
            <Field label="Labor Rate ($/hr)"><Input type="number" value={settings.labor_rate || 65} onChange={sf('labor_rate')} /></Field>
            <Field label="Tax Rate (%)"><Input type="number" value={settings.tax_rate || 0} onChange={sf('tax_rate')} step="0.1" /></Field>
            <Field label="Default Deposit (%)"><Input type="number" value={settings.deposit_pct || 50} onChange={sf('deposit_pct')} /></Field>
            <Field label="Invoice Terms"><Input value={settings.invoice_terms || 'Net 30'} onChange={sf('invoice_terms')} /></Field>
            <Field label="Invoice Footer" span2><Textarea value={settings.invoice_footer || ''} onChange={sf('invoice_footer')} /></Field>
          </div>
        </Card>
      )}

      {modal && (
        <UserModal
          user={modal.user}
          role={modal.type}
          onClose={() => setModal(null)}
          onSave={() => { load(); setModal(null); setToast({ msg: 'Saved ✓', type: 'success' }); }}
          onToast={t => setToast(t)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════
//  CUSTOMER MY PROJECTS PAGE
// ════════════════════════════════════════════════════════
export function MyProjectsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    supabase.from('jobs').select('*').eq('customer_id', user.id).order('created_at', { ascending: false }).then(({ data }) => {
      setJobs(data || []);
      setLoading(false);
    });
  }, [user]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="My Projects" sub="Track the progress of your roofing project" />
      {jobs.length === 0 && <Empty icon="🏠" title="No projects yet" sub="Contact us at (317) 555-0100 to get started." />}
      {jobs.map(j => {
        const phases = j.phases || [];
        const curr = phases.find(p => p.current && !p.done) || phases.find(p => !p.done);
        const doneCount = phases.filter(p => p.done).length;
        const bal = (j.contract_total || 0) - (j.amount_paid || 0);
        return (
          <Card key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} style={{ marginBottom: 12, cursor: 'pointer' }}>
            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ width: 48, height: 48, background: B.navy, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏠</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, color: '#94A3B8', fontSize: 13 }}>{j.id?.slice(0, 10)}</span>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{j.type}</span>
                  <Badge status={j.status} />
                </div>
                <div style={{ fontSize: 13, color: '#64748B', marginBottom: 4 }}>📍 {j.address}</div>
                {curr && <div style={{ fontSize: 12, color: '#D97706', fontWeight: 600, marginBottom: 8 }}>🔨 Current: {curr.label}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>
                  <span>{doneCount} of {phases.length} phases</span><span>{j.progress || 0}%</span>
                </div>
                <ProgressBar value={j.progress || 0} height={8} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {[['Contract', fmtCurrency(j.contract_total), '#F8FAFC', B.navy], ['Paid', fmtCurrency(j.amount_paid), '#F0FDF4', '#16A34A'], ['Balance', fmtCurrency(bal), bal > 0 ? '#FFFBEB' : '#F0FDF4', bal > 0 ? '#D97706' : '#16A34A']].map(([lbl, val, bg, col]) => (
                    <div key={lbl} style={{ flex: 1, background: bg, borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', fontSize: 12, minWidth: 90 }}>
                      <span style={{ color: '#94A3B8' }}>{lbl}</span>
                      <span style={{ fontWeight: 700, color: col }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
